import {
  KidComparisonData,
  KidDocumentForm,
} from '../../components/pages/raidr/kidsV2/types/KidPdfGeneratorV2.types';
import FastAPIInstance from '../../services/risksystem3-public-api';
import { KID_PDF_GENERATOR_BASE_URL } from '../../utilities/apiConstants';
import { AxiosInstance } from '../../utilities/requestClient';
import {
  KidOverviewDataItem,
  KidMarker,
  KidDocumentIdentifierPayload,
  KidFiguresRecordModel,
  KidDataResponse,
  CreateKidLayoutTemplatePayload,
  KidLayoutTemplateModel,
  KidLayoutStyleSummary,
} from './types';

export const generateKidDocument = async (payload: KidDocumentForm) => {
  const response = await AxiosInstance.post(
    `${KID_PDF_GENERATOR_BASE_URL}/raidr/kidv2/generate-kid-document`,
    payload,
    {
      headers: {
        accept: 'application/pdf',
      },
      responseType: 'blob',
    },
  );

  return response;
};

export const generateKidComparison = async (compareData: KidComparisonData) => {
  const response = await AxiosInstance.post(
    `${KID_PDF_GENERATOR_BASE_URL}/raidr/kidv2/generate-kid-comparison`,
    compareData,
    {
      headers: {
        accept: 'application/zip',
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
    },
  );
  return response;
};

export const getKidData = async (
  clientId: string,
  fundId: string,
  shareClass: string,
  documentLanguage: string,
  version?: string,
): Promise<KidDataResponse[]> => {
  const params = new URLSearchParams({
    document_language: documentLanguage,
  });

  if (version) {
    params.append('version', version);
  }

  return (
    await FastAPIInstance.get<KidDataResponse[]>(
      `/v1/kid/data/read/${clientId}/${fundId}/${shareClass}?${params.toString()}`,
    )
  ).data;
};

export const getKidOverviewData = async (
  clientId: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
) => {
  const params = new URLSearchParams();
  if (fundId) {
    params.append('fund_id', fundId);
  }
  if (shareClassId) {
    params.append('share_class_id', shareClassId);
  }
  if (documentLanguage) {
    params.append('document_language', documentLanguage);
  }

  const path = `/v1/kid/data/latest_overview/${clientId}`;
  return (
    await FastAPIInstance.get<KidOverviewDataItem[]>(
      `${path}${params.toString() ? `?${params.toString()}` : ''}`,
    )
  ).data;
};

export const getKidOverviewHistoricalData = async (
  clientId: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
) => {
  const params = new URLSearchParams();

  if (fundId) {
    params.append('fund_id', fundId);
  }
  if (shareClassId) {
    params.append('share_class_id', shareClassId);
  }
  if (documentLanguage) {
    params.append('document_language', documentLanguage);
  }

  return (
    await FastAPIInstance.get<Record<string, KidOverviewDataItem[]>>(
      `/v1/kid/data/history_overview/${clientId}${
        params.toString() ? `?${params.toString()}` : ''
      }`,
    )
  ).data;
};

export const updateOverviewStatus = async (
  clientId: string,
  fundId: string,
  shareClassId: string,
  statusChangeTo: 'edited' | 'editing',
  documentLanguage: string,
) => {
  const params = new URLSearchParams({
    status_change_to: statusChangeTo,
    document_language: documentLanguage,
  });

  return (
    await FastAPIInstance.put<KidOverviewDataItem>(
      `/v1/kid/data/update_overview_status/${clientId}/${fundId}/${shareClassId}?${params.toString()}`,
    )
  ).data;
};

export type KidCostVariables = {
  client_id: string;
  fund_id: string;
  share_class_id: string;
  entry_cost: number;
  exit_cost: number;
  ongoing_cost: number;
  transaction_cost: number;
  applied_performance_fee: number;
  notional_performance_fee: number;
  has_performance_fee: boolean;
  timestamp: string;
};

export type CreateKidCostVariablesPayload = Omit<
  KidCostVariables,
  'timestamp'
>;

export const getKidCostVariables = async (
  clientId: string,
  fundId: string,
  shareClassId: string,
) => {
  return (
    await FastAPIInstance.get<KidCostVariables>(
      `/v1/kid/cost_variables/${clientId}/${fundId}/${shareClassId}/latest`,
    )
  ).data;
};

export const createKidCosts = async (payload: CreateKidCostVariablesPayload) => {
  return (
    await FastAPIInstance.post<KidCostVariables>(
      `/v1/kid/cost_variables`,
      payload,
    )
  ).data;
};

export type CalculateKidFiguresPayload = {
  client_id: string;
  fund_id: string;
  share_class_id: string;
  /** Reference date (YYYY-MM-DD), normalised to the reporting Friday on the backend. */
  date?: string;
};

export const calculateKidFigures = async (
  payload: CalculateKidFiguresPayload,
) => {
  const { fund_id, share_class_id, date } = payload;

  const params = new URLSearchParams();
  if (date) {
    params.append('date', date);
  }

  const queryString = params.toString() ? `?${params.toString()}` : '';

  return (
    await AxiosInstance.post<KidFiguresRecordModel>(
      `kid_figures/calculate/${encodeURIComponent(fund_id)}/${encodeURIComponent(share_class_id)}${queryString}`,
    )
  ).data;
};

export const getLatestKidFigures = async (
  clientId: string,
  fundId: string,
  shareClassId?: string,
): Promise<KidFiguresRecordModel[] | null> => {
  const params = new URLSearchParams();
  if (shareClassId) {
    params.append('share_class_id', shareClassId);
  }

  return (
    await FastAPIInstance.get<KidFiguresRecordModel[] | null>(
      `/v1/kid/figures/latest/${clientId}/${fundId}${
        params.toString() ? `?${params.toString()}` : ''
      }`,
    )
  ).data;
};

export const getKidCompareData = async (
  clientId: string,
  fundId: string,
  shareClassId: string,
  comparisonVersion: string,
  targetVersion: string,
  documentLanguage: string = 'English',
) => {
  const params = new URLSearchParams({
    comparison_version: comparisonVersion,
    target_version: targetVersion,
    document_language: documentLanguage,
  });

  return (
    await FastAPIInstance.get<{
      comparison_version: KidOverviewDataItem[];
      target_version: KidOverviewDataItem[];
    }>(
      `/v1/kid/data/compare_kid_document/${clientId}/${fundId}/${shareClassId}?${params.toString()}`,
    )
  ).data;
};

export const getKidMarkerAll = async (
  clientId: string,
  fundId: string,
  shareClassId: string,
  kidFigureCalculationDate?: string,
  overviewLastUpdatingDatetime?: string,
  withValue: boolean = true,
): Promise<KidMarker[]> => {
  const params = new URLSearchParams();

  if (kidFigureCalculationDate) {
    params.append('kid_figure_calculation_date', kidFigureCalculationDate);
  }
  if (overviewLastUpdatingDatetime) {
    params.append(
      'overview_last_updating_datetime',
      overviewLastUpdatingDatetime,
    );
  }
  if (withValue !== undefined) {
    params.append('with_value', withValue.toString());
  }

  return (
    await FastAPIInstance.get<KidMarker[]>(
      `/v1/kid/marker/all/${clientId}/${fundId}/${shareClassId}${
        params.toString() ? `?${params.toString()}` : ''
      }`,
    )
  ).data;
};

export const revokeReviewKid = async (
  payload: KidDocumentIdentifierPayload,
) => {
  return FastAPIInstance.post('/v1/kid/data/revoke_review', payload);
};

export const getJurisdictions = async (): Promise<string[]> => {
  return (await FastAPIInstance.get<string[]>('/v1/kid/data/jurisdictions'))
    .data;
};

export const getLanguages = async (): Promise<string[]> => {
  return (await FastAPIInstance.get<string[]>('/v1/kid/data/languages')).data;
};

type LatestMajorPublishedVersion = {
  version: string;
  data: any[];
};

export const getLatestMajorPublishedVersion = async (
  clientId: string,
  fundId: string,
  shareClassId: string,
) => {
  return (
    await FastAPIInstance.get<LatestMajorPublishedVersion>(
      `/v1/kid/data/read_latest_major_published/${clientId}/${fundId}/${shareClassId}/English`,
    )
  ).data;
};

export type KidLayoutStyles = {
  [key: string]: KidLayoutStyleSummary[];
};

export const getAllKidLayoutStyles = async (clientId: string) => {
  return (
    await FastAPIInstance.get<KidLayoutStyles>(
      `/v1/kid/layout_style/available/${clientId}`,
    )
  ).data;
};

export const createKidLayoutTemplate = async (
  payload: CreateKidLayoutTemplatePayload,
) => {
  return (
    await FastAPIInstance.post<{ success: boolean }>(
      '/v1/kid/layout_style/create',
      payload,
    )
  ).data;
};

export type ReadKidLayoutTemplateParams = {
  kidLayoutId: string;
  kidLayoutLastUpdatingTimestamp: string;
  fundId?: string;
  shareClassId?: string;
  kidFigureCalculationDate?: string;
};

export const readKidLayoutTemplate = async ({
  kidLayoutId,
  kidLayoutLastUpdatingTimestamp,
  fundId,
  shareClassId,
  kidFigureCalculationDate,
}: ReadKidLayoutTemplateParams) => {
  const params = new URLSearchParams({
    kid_layout_last_updating_timestamp: kidLayoutLastUpdatingTimestamp,
  });

  if (fundId) {
    params.append('fund_id', fundId);
  }

  if (shareClassId) {
    params.append('share_class_id', shareClassId);
  }

  if (kidFigureCalculationDate) {
    params.append(
      'kid_figure_calculation_date',
      kidFigureCalculationDate,
    );
  }

  return (
    await FastAPIInstance.get<KidLayoutTemplateModel[]>(
      `/v1/kid/layout_style/read/${kidLayoutId}?${params.toString()}`,
    )
  ).data;
};
